/**
 * teleop-logs/format — framework-free formatting & parsing for the log panels.
 *
 * INTERNAL module file: it is NOT re-exported through index.ts. All parsing,
 * compaction, tokenization and line-building stays inside the module.
 */

const DC_LINE_MAX_LEN = 140;

export interface ParsedDcMessage {
  id: string;
  time: string;
  tag: string;
  body: string;
  preview: string;
  detail: string;
  variant: string;
}

export type JsonTokenType = "key" | "str" | "num" | "text";

export interface JsonToken {
  type: JsonTokenType;
  value: string;
}

function truncateLine(s: string, max = DC_LINE_MAX_LEN): string {
  const t = s.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

/** Pretty-print JSON for expand detail; fall back to raw text. */
export function formatDcDetail(raw: unknown): string {
  const text = String(raw ?? "").trim();
  if (!text) return "";
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
}

function compactTelemetryPayload(event: string, payload: unknown): string {
  const ev = String(event || "?");
  if (payload == null) return "{}";

  if (ev === "telemetry" && typeof payload === "object" && !Array.isArray(payload)) {
    const p = payload as Record<string, unknown>;
    const status =
      p.robot_status && typeof p.robot_status === "object"
        ? (p.robot_status as Record<string, unknown>)
        : {};
    const motion =
      p.motion_state && typeof p.motion_state === "object"
        ? (p.motion_state as Record<string, unknown>)
        : {};
    const velocity =
      motion.velocity && typeof motion.velocity === "object"
        ? (motion.velocity as Record<string, unknown>)
        : {};
    const o: Record<string, unknown> = {};
    if (status.battery_percent != null) o.battery = status.battery_percent;
    if (status.mode != null) o.mode = status.mode;
    if (motion.state != null) o.state = motion.state;
    if (velocity.vx != null) o.vx = velocity.vx;
    if (velocity.vy != null) o.vy = velocity.vy;
    if (velocity.vyaw != null) o.vyaw = velocity.vyaw;
    if (Array.isArray(p.cargo) && p.cargo.length) {
      o.cargo = (p.cargo as Array<{ id: string; lid: string }>)
        .map((c) => `${c.id}:${c.lid}`)
        .join(",");
    }
    return JSON.stringify(o);
  }

  if (ev === "robot-config" && typeof payload === "object" && !Array.isArray(payload)) {
    const p = payload as Record<string, unknown>;
    const limits =
      p.limits && typeof p.limits === "object" ? (p.limits as Record<string, unknown>) : {};
    const start =
      p.start_velocity && typeof p.start_velocity === "object"
        ? (p.start_velocity as Record<string, unknown>)
        : {};
    const robot =
      p.robot && typeof p.robot === "object" ? (p.robot as Record<string, unknown>) : {};
    return JSON.stringify({
      // mmr (protocol v1): cameras, accepted input sources, planner link.
      cameras: Array.isArray(p.cameras)
        ? p.cameras.map((c: { mount?: unknown }) => String(c?.mount ?? "?")).join(",")
        : undefined,
      input_sources: Array.isArray(p.input_sources) ? p.input_sources.join(",") : undefined,
      ros: robot.link,
      // upstream (quadruped) fields — absent for our robot, dropped by JSON.stringify.
      units: p.units,
      max_vx: limits.max_vx,
      max_vy: limits.max_vy,
      max_vyaw: limits.max_vyaw,
      start_vx: start.vx,
      start_vy: start.vy,
      start_vyaw: start.vyaw,
      cargo: Array.isArray(p.cargo_boxes) ? p.cargo_boxes.length : undefined,
    });
  }

  if (ev === "capabilities-error" && typeof payload === "object" && !Array.isArray(payload)) {
    const p = payload as Record<string, unknown>;
    return JSON.stringify({ code: p.code, detail: p.detail });
  }

  if (ev === "link-quality" && typeof payload === "object" && !Array.isArray(payload)) {
    const p = payload as Record<string, unknown>;
    const o: Record<string, unknown> = { level: p.level };
    if (p.rtt_ms != null) o.rtt_ms = p.rtt_ms;
    if (p.loss_pct != null) o.loss_pct = p.loss_pct;
    if (p.abr_profile != null) o.abr = p.abr_profile;
    if (p.abr_fps != null) o.fps = p.abr_fps;
    if (p.abr_bitrate_kbps != null) o.kbps = p.abr_bitrate_kbps;
    if (Array.isArray(p.active_sources)) o.active = p.active_sources;
    return JSON.stringify(o);
  }

  if (ev === "camera-control" && typeof payload === "object" && !Array.isArray(payload)) {
    const p = payload as Record<string, unknown>;
    return JSON.stringify({ mount: p.mount, enabled: p.enabled });
  }

  if (ev === "send-cargo" && typeof payload === "object" && !Array.isArray(payload)) {
    const p = payload as Record<string, unknown>;
    return JSON.stringify({ type: p.type, box_id: p.box_id });
  }

  if (typeof payload === "object") {
    return truncateLine(JSON.stringify(payload));
  }
  return truncateLine(String(payload));
}

function telemetryVariant(event: string): string {
  const ev = String(event || "").toLowerCase();
  if (ev === "telemetry") return "telemetry-status";
  if (ev === "robot-config") return "telemetry-config";
  if (ev === "capabilities-error") return "telemetry-config-error";
  if (ev === "link-quality") return "telemetry-link-quality";
  if (ev === "camera-control") return "telemetry-echo";
  if (ev === "send-cargo") return "out";
  return "telemetry-unknown";
}

let dcEntrySeq = 0;

function nextDcEntryId(): string {
  dcEntrySeq += 1;
  return `dc-${dcEntrySeq}-${Date.now().toString(36)}`;
}

/**
 * Parse one DataChannel message — telemetry/config from the robot, or a command the
 * operator just sent — into a log entry. Both directions are always `{event, payload}`
 * JSON by construction (every teleop-commands dispatch, via `Dispatcher`/`MotionStream`
 * in commands.ts/motion-stream.ts, and every robot message, is shaped that way), so one
 * parser covers both; only which array a caller pushes the result into (`incoming` vs
 * `outgoing`) distinguishes them.
 */
export function parseDcMessage(raw: unknown): ParsedDcMessage {
  const message = String(raw ?? "").trim();
  const time = new Date().toLocaleTimeString();
  let tag = "[?]";
  let body = truncateLine(message);
  let variant = "telemetry-unknown";

  try {
    const parsed = JSON.parse(message) as Record<string, unknown>;
    const event = String(parsed.event ?? parsed.type ?? "?");
    tag = `[${event}]`;
    const payload = parsed.payload ?? parsed.data ?? parsed;
    body = compactTelemetryPayload(event, payload);
    variant = telemetryVariant(event);
  } catch {
    /* raw */
  }

  return {
    id: nextDcEntryId(),
    time,
    tag,
    body,
    preview: body,
    detail: formatDcDetail(message),
    variant,
  };
}

/** Split compact JSON into React-friendly spans. */
export function tokenizeCompactJson(text: string): JsonToken[] {
  const re =
    /"([^"\\]*(?:\\.[^"\\]*)*)"\s*:|"([^"\\]*(?:\\.[^"\\]*)*)"|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?|true|false|null/g;
  const tokens: JsonToken[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) {
      tokens.push({ type: "text", value: text.slice(last, m.index) });
    }
    if (m[1] !== undefined) {
      tokens.push({ type: "key", value: `"${m[1]}":` });
    } else if (m[2] !== undefined) {
      tokens.push({ type: "str", value: `"${m[2]}"` });
    } else {
      tokens.push({
        type: m[0] === "true" || m[0] === "false" || m[0] === "null" ? "key" : "num",
        value: m[0],
      });
    }
    last = re.lastIndex;
  }
  if (last < text.length) {
    tokens.push({ type: "text", value: text.slice(last) });
  }
  if (!tokens.length) tokens.push({ type: "text", value: text });
  return tokens;
}

/**
 * Build a single system/diagnostic line, e.g. `[HH:MM:SS] message`.
 */
export function formatSystemLine(text: unknown, meta: { level?: string } = {}): string {
  const level = meta.level ?? "info";
  const line = `[${new Date().toLocaleTimeString()}] ${String(text ?? "")}`;
  if (level === "debug") console.debug(line);
  else if (level === "warn") console.warn(line);
  else if (level === "error") console.error(line);
  else console.info(line);
  return line;
}
